/**
 * @module  public/js/stores/dealsstore.js
 * @class   DealsStore
 * @summary Central cache for deals + pipeline meta with a tiny pub/sub.
 *
 * ## Dependencies
 * { apiClient      – expects fetchJson(),
 *   authService    – expects requireAuth(),
 *   crmConnectionService,
 *   endpoints      – DI instance from endpoints-service.js }
 */

export default class DealsStore {
  /* ── DI singletons ─────────────────────────────────────────── */
  #apiClient;
  #auth;
  #crmConn;
  #endpoints;

  /* ── in-memory caches ──────────────────────────────────────── */
  #pipelines = []; // Array<Pipeline>
  #pipelineLUT = Object.create(null); // pipelineId → { stageId → label }
  #deals = new Map(); // dealId → deal
  #accountsCache = null; // derived helper for Kanban

  /* ── pub/sub ───────────────────────────────────────────────── */
  #listeners = { change: new Set() };

  constructor({ apiClient, authService, crmConnectionService, endpoints }) {
    if (!apiClient || !authService || !crmConnectionService || !endpoints) {
      throw new Error('DealsStore: missing dependencies');
    }
    this.#apiClient = apiClient;
    this.#auth = authService;
    this.#crmConn = crmConnectionService;
    this.#endpoints = endpoints;
  }

  /* ============================================================
   * PIPELINES
   * ============================================================
   */
  async #fetchPipelines(force = false) {
    if (this.#pipelines.length && !force) return this.#pipelines;

    const user = await this.#auth.requireAuth();
    const crm = await this.#crmConn.getConnectedProvider();
    const url = this.#endpoints.getPipelines({ userId: user.uid, crm });

    const { pipelines = [] } = await this.#apiClient.fetchJson(url, {
      auth: true,
    });
    this.#pipelines = pipelines;

    // build lookup table once
    this.#pipelineLUT = Object.create(null);
    for (const p of pipelines) {
      this.#pipelineLUT[p.pipelineId] = Object.fromEntries(
        (p.stages ?? []).map((s) => [s.stageId, s.label])
      );
    }
    this.#emit('change');
    return pipelines;
  }

  /** Public facade used by Kanban */
  getPipelineData(force = false) {
    return this.#fetchPipelines(force);
  }

  getStageLabel(pipeId, stageId) {
    return this.#pipelineLUT?.[pipeId]?.[stageId] ?? stageId;
  }

  /* ================================================================
   * DEALS (derived from “accounts-by-owner”)
   * ==============================================================*/
  async #fetchDeals(force = false) {
    if (!force && this.#deals.size) return [...this.#deals.values()];

    /* 1) fetch accounts the signed-in user owns */
    const user = await this.#auth.requireAuth();
    const crm = await this.#crmConn.getConnectedProvider();
    const acctUrl = this.#endpoints.getAccountsByOwner({
      userId: user.uid,
      crm,
    });
    const { results: accounts = [] } = await this.#apiClient.fetchJson(
      acctUrl,
      { auth: true }
    );

    if (!accounts.length) {
      this.#deals.clear();
      this.#accountsCache = [];
      this.#emit('change');
      return [];
    }

    /* 2) per-account deal requests — throttle to avoid HubSpot burst limit */
    const MAX_WORKERS = 2; // concurrent CF calls
    const DELAY_MS = 250; // pause between calls
    const queue = [...accounts]; // shallow copy so we can shift()
    const bag = []; // collect deals here

    const worker = async () => {
      const acct = queue.shift();
      if (!acct) return; // queue empty: worker can finish

      const qs = { userId: user.uid, accountId: acct.id, crm };
      const url =
        crm === 'salesforce'
          ? this.#endpoints.getDealDetails(qs) // supports ?accountId
          : this.#endpoints.getAccountDeals(qs); // HubSpot fallback

      try {
        const { deals = [] } = await this.#apiClient.fetchJson(url, {
          auth: true,
        });
        deals.forEach((d) => {
          d.account = acct;
          bag.push(d);
        });
      } catch (err) {
        console.warn(
          '[DealsStore] get*Deals failed for account',
          acct.id,
          err.message
        );
        /* optional: store failed acct.id for later UX */
      }

      await new Promise((r) => setTimeout(r, DELAY_MS)); // polite pause
      return worker(); // recurse for next acct
    };

    await Promise.all(
      new Array(Math.min(MAX_WORKERS, queue.length)).fill(null).map(worker)
    );

    /* 3) rebuild cache & notify listeners */
    this.#deals.clear();
    bag.forEach((d) => this.#deals.set(d.id, d));
    this.#accountsCache = null; // bust derived cache
    this.#emit('change');
    return bag;
  }

  /* ============================================================
   * SINGLE DEAL
   * ============================================================
   */
  async getDealById(dealId, { refresh = false } = {}) {
    if (!refresh && this.#deals.has(dealId)) return this.#deals.get(dealId);

    const user = await this.#auth.requireAuth();
    const crm = await this.#crmConn.getConnectedProvider();
    const url = this.#endpoints.getDealDetails({
      userId: user.uid,
      dealId,
      crm,
    });

    const { deal } = await this.#apiClient.fetchJson(url, { auth: true });
    this.#deals.set(dealId, deal);
    this.#accountsCache = null;
    this.#emit('change');
    return deal;
  }

  /** Optimistic in-place merge so UI feels instant */
  mergeDealProps(id, patch = {}) {
    if (!this.#deals.has(id)) return;
    const d = this.#deals.get(id);
    d.properties = { ...d.properties, ...patch };
    this.#deals.set(id, d);
    this.#emit('change');
  }

  /* ===== accounts-with-deals helper (used by Kanban) ========= */
  /**
   * Return an array of accounts, each with its `.deals` array populated.
   * Results are cached until any deal reloads.
   *
   * @param {boolean} [force=false] – bypass the derived cache
   * @returns {Promise<Array<{ id:string, properties:object, deals:Array }>>}
   */
  async getAccountsWithDeals(force = false) {
    if (this.#accountsCache && !force) return this.#accountsCache;

    // ensure the base deals cache is ready
    const deals = await this.#fetchDeals(force);
    const map = new Map(); // accountId → { …account, deals: [] }

    for (const d of deals) {
      const acct = d.account; // added in #fetchDeals()
      if (!acct) continue; // safety guard
      if (!map.has(acct.id)) map.set(acct.id, { ...acct, deals: [] });
      map.get(acct.id).deals.push(d);
    }

    this.#accountsCache = [...map.values()];
    return this.#accountsCache;
  }

  /* ============================================================
   * Pub/Sub
   * ============================================================
   */
  on(evt, fn) {
    this.#listeners[evt]?.add(fn);
  }
  off(evt, fn) {
    this.#listeners[evt]?.delete(fn);
  }

  #emit(evt) {
    this.#listeners[evt]?.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
  }
}
